import {
  DynamicDrawUsage,
  IcosahedronGeometry,
  InstancedMesh,
  Mesh,
  Object3D,
  PlaneGeometry,
  TetrahedronGeometry,
  Vector3
} from 'three';
import { Ability, AbilityPhase } from './Ability.js';
import { RibbonGeometry, RibbonMode } from '../effects/RibbonGeometry.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { LAYER } from '../core/Layers.js';
import { frame } from '../core/FrameUniforms.js';
import { settings } from '../config/settings.js';
import { getColor } from '../utils/color.js';
import { Easing, lerp, randRange, saturate, smoothstep } from '../utils/math.js';
import {
  createShadowDistortionMaterial,
  createShadowSilhouetteMaterial,
  createShadowTrailMaterial,
  syncShadowMaterial
} from '../materials/ShadowStepMaterial.js';

const TAU = Math.PI * 2;
const BODY_PARTS = Object.freeze([
  [0, 1.58, 0, 0.22, 0.24, 0.21, 0, 0, 0],
  [0, 1.13, 0, 0.31, 0.48, 0.2, 0, 0, 0],
  [0, 0.72, 0, 0.27, 0.2, 0.18, 0, 0, 0],
  [-0.34, 1.13, 0, 0.105, 0.42, 0.105, 0, 0, -0.16],
  [0.34, 1.13, 0, 0.105, 0.42, 0.105, 0, 0, 0.16],
  [-0.2, 0.39, 0, 0.12, 0.4, 0.12, 0, 0, -0.055],
  [0.2, 0.39, 0, 0.12, 0.4, 0.12, 0, 0, 0.055],
  [-0.2, 0.08, 0.07, 0.13, 0.16, 0.22, 0, 0, 0],
  [0.2, 0.08, 0.07, 0.13, 0.16, 0.22, 0, 0, 0]
]);
const BODY_COUNT = BODY_PARTS.length;
const MAX_FRAGMENTS = 28;
const TRAIL_SEGMENTS = 36;
let nextParticleOwner = 1000;

const _dummy = new Object3D();
const _emit = {};
const _pos = new Vector3();
const _dir = new Vector3();
const _side = new Vector3();
const _scale = new Vector3();
const _rotation = new Vector3();
const _distortionScale = new Vector3();

/**
 * Passo Sombrio is deliberately one pooled cast with two player-facing beats.
 * The first traversal leaves a readable anchor; the same live instance owns the
 * reactivation, exact return position, expiry and delayed cooldown.
 */
export class ShadowStepAbility extends Ability {
  constructor(context) {
    super('shadowstep', context);
    this._particleOwner = nextParticleOwner++;
    this._ownedParticleSystems = new Set([
      'shadowstep.motes',
      'shadowstep.smoke',
      'shadowstep.streaks',
      'shadowstep.chips'
    ]);
  }

  createShaders() {
    this.bodyGeometry = new IcosahedronGeometry(1, 2);
    this.fragmentGeometry = new TetrahedronGeometry(1, 0);
    this.distortionGeometry = new IcosahedronGeometry(1, 3);

    this.silhouetteMaterial = createShadowSilhouetteMaterial();
    this.outlineMaterial = createShadowSilhouetteMaterial({ additive: true });
    this.afterimageMaterialA = createShadowSilhouetteMaterial({ additive: true });
    this.afterimageMaterialB = createShadowSilhouetteMaterial({ additive: true });
    this.eyeMaterial = createShadowSilhouetteMaterial({ additive: true });
    this.fragmentMaterial = createShadowSilhouetteMaterial({ additive: true });

    this.silhouette = this._createBodyMesh(this.silhouetteMaterial, 10);
    this.outline = this._createBodyMesh(this.outlineMaterial, 11);
    this.afterimageA = this._createBodyMesh(this.afterimageMaterialA, 9);
    this.afterimageB = this._createBodyMesh(this.afterimageMaterialB, 8);
    this.bodyMeshes = [this.silhouette, this.outline, this.afterimageA, this.afterimageB];

    this.eyes = new InstancedMesh(this.bodyGeometry, this.eyeMaterial, 2);
    this.eyes.layers.set(LAYER.VFX);
    this.eyes.renderOrder = 14;
    this.eyes.frustumCulled = false;
    this.eyes.count = 2;
    this.eyes.instanceMatrix.setUsage(DynamicDrawUsage);
    this.group.add(this.eyes);

    this.fragments = new InstancedMesh(this.fragmentGeometry, this.fragmentMaterial, MAX_FRAGMENTS);
    this.fragments.layers.set(LAYER.VFX);
    this.fragments.renderOrder = 13;
    this.fragments.frustumCulled = false;
    this.fragments.count = 0;
    this.fragments.instanceMatrix.setUsage(DynamicDrawUsage);
    this.group.add(this.fragments);
    this.anchorPoseMeshes = [...this.bodyMeshes, this.eyes, this.fragments];

    this.fragmentRecords = [];
    for (let index = 0; index < MAX_FRAGMENTS; index++) {
      this.fragmentRecords.push({ angle: 0, radius: 0, height: 0, speed: 0, size: 0, phase: 0 });
    }

    this.trailPoints = [];
    for (let index = 0; index <= TRAIL_SEGMENTS; index++) this.trailPoints.push(new Vector3());
    this.trailRibbon = new RibbonGeometry(TRAIL_SEGMENTS);
    this.trailCoreRibbon = new RibbonGeometry(TRAIL_SEGMENTS);
    this.trailMaterial = createShadowTrailMaterial();
    this.trailCoreMaterial = createShadowTrailMaterial();
    this.trail = new Mesh(this.trailRibbon.geometry, this.trailMaterial);
    this.trailCore = new Mesh(this.trailCoreRibbon.geometry, this.trailCoreMaterial);
    for (const [mesh, order] of [[this.trail, 11], [this.trailCore, 12]]) {
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = order;
      mesh.frustumCulled = false;
      mesh.matrixAutoUpdate = false;
      this.group.add(mesh);
    }
    this._trailOptions = {
      count: TRAIL_SEGMENTS + 1,
      width: 1,
      mode: RibbonMode.BILLBOARD,
      cameraPosition: this.ctx.camera.position,
      widthProfile: (t) => 0.34 + Math.sin(Math.PI * t) * 0.8 + Math.sin(t * 17.0) * 0.08
    };
    this._trailCoreOptions = {
      count: TRAIL_SEGMENTS + 1,
      width: 1,
      mode: RibbonMode.BILLBOARD,
      cameraPosition: this.ctx.camera.position,
      widthProfile: (t) => 0.18 + Math.sin(Math.PI * t) * 0.42
    };

    this.groundGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.groundMaterial = createShadowTrailMaterial();
    this.groundEcho = new Mesh(this.groundGeometry, this.groundMaterial);
    this.groundEcho.layers.set(LAYER.VFX);
    this.groundEcho.renderOrder = 7;
    this.groundEcho.frustumCulled = false;
    this.group.add(this.groundEcho);

    this.distortionMaterial = createShadowDistortionMaterial();
    this.distortion = new InstancedMesh(this.distortionGeometry, this.distortionMaterial, 2);
    this.distortion.layers.set(LAYER.DISTORTION);
    this.distortion.renderOrder = 4;
    this.distortion.frustumCulled = false;
    this.distortion.count = 0;
    this.distortion.instanceMatrix.setUsage(DynamicDrawUsage);
    this.group.add(this.distortion);

    this._anchorPosition = new Vector3();
    this._destination = new Vector3();
    this._swapOrigin = new Vector3();
    this._pathStart = new Vector3();
    this._pathEnd = new Vector3();
    this._seed = 0;
    this._mode = 'idle';
    this._stepTime = 0;
    this._swapTime = 0;
    this._trailLingerTime = 0;
    this._cooldownStarted = false;
    this._residualActive = false;
    this._vanishPeakFired = false;
    this._arrivalFired = false;
    this._swapPeakFired = false;
    this._visualState = {
      fade: 0,
      reveal: 0,
      pulse: 0,
      instability: 0,
      opacity: 1,
      progress: 1,
      bidirectional: 0,
      intensity: 1,
      seed: 0
    };
  }

  _createBodyMesh(material, renderOrder) {
    const mesh = new InstancedMesh(this.bodyGeometry, material, BODY_COUNT);
    mesh.layers.set(LAYER.VFX);
    mesh.renderOrder = renderOrder;
    mesh.frustumCulled = false;
    mesh.count = BODY_COUNT;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.group.add(mesh);
    return mesh;
  }

  createParticles() {
    const particles = this.ctx.particles;
    this.motes = particles.get('shadowstep.motes', {
      capacity: 4200,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      swirl: true,
      softFade: 0.35
    });
    this.motes.uniforms.uDrag.value = 1.25;
    this.motes.uniforms.uEndSize.value = 0.12;
    this.motes.uniforms.uSizeIn.value = 0.035;
    this.motes.uniforms.uFadeIn.value = 0.05;
    this.motes.uniforms.uFadeOut.value = 0.42;

    this.smoke = particles.get('shadowstep.smoke', {
      capacity: 2600,
      shape: ParticleShape.SMOKE,
      additive: false,
      curl: true,
      softFade: 1.15
    });
    this.smoke.uniforms.uDrag.value = 1.75;
    this.smoke.uniforms.uEndSize.value = 3.2;
    this.smoke.uniforms.uSizeIn.value = 0.12;
    this.smoke.uniforms.uFadeIn.value = 0.12;
    this.smoke.uniforms.uFadeOut.value = 0.34;

    this.streaks = particles.get('shadowstep.streaks', {
      capacity: 3600,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      curl: true,
      softFade: 0.28
    });
    this.streaks.uniforms.uDrag.value = 0.75;
    this.streaks.uniforms.uEndSize.value = 0.18;
    this.streaks.uniforms.uSizeIn.value = 0.02;
    this.streaks.uniforms.uFadeIn.value = 0.025;
    this.streaks.uniforms.uFadeOut.value = 0.52;

    this.chips = particles.get('shadowstep.chips', {
      capacity: 2200,
      shape: ParticleShape.CHIP,
      additive: false,
      lit: true,
      softFade: 0.22
    });
    this.chips.uniforms.uDrag.value = 0.42;
    this.chips.uniforms.uEndSize.value = 0.62;
    this.chips.uniforms.uFadeOut.value = 0.68;

    this.intakeEmitter = new RateEmitter();
    this.anchorMoteEmitter = new RateEmitter();
    this.smokeEmitter = new RateEmitter();
    this.pathEmitter = new RateEmitter();
  }

  get instanceCount() {
    return BODY_COUNT * 4 + 2 + this.fragments.count;
  }

  get impactDuration() {
    return Math.max(0.05, settings.shadowstep.shadowDuration * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.05, settings.shadowstep.dissipateDuration);
  }

  get canReactivate() {
    return this.phase === AbilityPhase.IMPACT && this._residualActive && this._mode === 'anchor';
  }

  get reactivationRatio() {
    if (!this.canReactivate) return 0;
    return saturate(1 - this.impactTime / this.impactDuration);
  }

  onSpawn() {
    const c = settings.shadowstep;
    this._mode = 'step';
    this._stepTime = 0;
    this._swapTime = 0;
    this._trailLingerTime = 0;
    this._cooldownStarted = false;
    this._residualActive = false;
    this._vanishPeakFired = false;
    this._arrivalFired = false;
    this._swapPeakFired = false;
    this._seed = Math.random() * 100;
    this._anchorPosition.copy(this.origin);
    this.pointAt(1, this._destination);
    this._destination.y = 0;
    this._pathStart.copy(this.origin).setY(c.pathHeight);
    this._pathEnd.copy(this._destination).setY(c.pathHeight);

    this.intakeEmitter.reset();
    this.anchorMoteEmitter.reset();
    this.smokeEmitter.reset();
    this.pathEmitter.reset();
    for (const record of this.fragmentRecords) {
      record.angle = Math.random() * TAU;
      record.radius = Math.random();
      record.height = Math.random();
      record.speed = randRange(0.72, 1.3);
      record.size = Math.random();
      record.phase = Math.random() * TAU;
    }

    this._setCharacterVisual(1, 0);
    this._poseBodyInstances();
    this._setAnchorVisible(true);
    this._syncParticleUniforms();
    this._castStartFx();
  }

  /** Consume the live residual without arming a second aim. */
  reactivate() {
    if (!this.canReactivate) return false;
    this._mode = 'swap';
    this._residualActive = false;
    this._swapTime = 0;
    this._swapPeakFired = false;
    this._swapOrigin.copy(this.ctx.character?.position ?? this._destination).setY(0);
    this._pathStart.copy(this._swapOrigin).setY(settings.shadowstep.pathHeight);
    this._pathEnd.copy(this._anchorPosition).setY(settings.shadowstep.pathHeight);
    this._swapReactionFx();
    return true;
  }

  update(dt) {
    if (!this.isActive) return;
    this.age += dt;
    this._syncParticleUniforms();

    if (this.phase === AbilityPhase.TRAVEL) {
      this._updateFirstStep(dt);
    } else if (this.phase === AbilityPhase.IMPACT) {
      if (this._mode === 'swap') this._updateSwap(dt);
      else this._updateAnchor(dt);
    } else if (this.phase === AbilityPhase.FADE) {
      this._updateDissipation(dt);
    }

    this._updateLight(dt, this.phase === AbilityPhase.FADE ? Math.max(0, 1 - this.fadeTime / this.fadeDuration) : 1);
  }

  _updateFirstStep(dt) {
    const c = settings.shadowstep;
    this._stepTime += dt;
    const disappear = Math.max(0.01, c.disappearTime);
    const reappear = Math.max(0.01, c.reappearTime);
    const total = disappear + reappear;
    const vanish = saturate(this._stepTime / disappear);
    const rebuild = saturate((this._stepTime - disappear) / reappear);
    const movement = smoothstep(0.18, 0.86, saturate(this._stepTime / total));

    this.front = movement * this.length;
    this.u = movement;
    _pos.copy(this.origin).lerp(this._destination, Easing.inOutCubic(movement));
    this._moveCharacter(_pos);
    this.position.copy(_pos).setY(0.9);

    let opacity = 1 - Easing.inCubic(saturate((vanish - 0.12) / 0.75));
    if (this._stepTime >= disappear) opacity = Easing.outCubic(rebuild);
    const compression = Math.sin(Math.min(1, vanish) * Math.PI) * (1 - rebuild);
    this._setCharacterVisual(opacity, compression);

    if (!this._vanishPeakFired && vanish >= 0.62) {
      this._vanishPeakFired = true;
      this._vanishFx(this.origin, this.direction);
    }
    if (!this._arrivalFired && rebuild >= 0.08) {
      this._arrivalFired = true;
      this._arrivalFx(this._destination, this.direction);
    }

    const reveal = Easing.outCubic(saturate((vanish - 0.24) / 0.7));
    const trailFade = Math.sin(Math.PI * saturate(this._stepTime / total));
    this._updateAnchorVisual(dt, reveal, 0.12, 0, reveal);
    this._updateTrail(trailFade, movement, 0);
    this._updateDistortion(trailFade, 1 + compression * 0.7, true);
    this._prepFx(dt, this.ctx.character?.position ?? _pos, vanish);
    this._pathFx(dt, movement, 0);
    this.ctx.shake.rumble(c.travelRumble * settings.global.cameraShake, dt);

    if (this._stepTime >= total) {
      this._moveCharacter(this._destination);
      this._setCharacterVisual(1, 0);
      this.phase = AbilityPhase.IMPACT;
      this.impactTime = 0;
      this._mode = 'anchor';
      this._residualActive = true;
      this._trailLingerTime = 0;
      this.u = 1;
      this.front = this.length;
      this.position.copy(this._destination).setY(0.9);
    }
  }

  _updateAnchor(dt) {
    const c = settings.shadowstep;
    this.impactTime += dt;
    this._trailLingerTime += dt;
    const ratio = saturate(this.impactTime / this.impactDuration);
    const warning = smoothstep(c.expiryWarning, 1, ratio);
    const pulse = 0.18 + Math.sin(this.age * lerp(c.anchorPulseSpeed, c.expiryPulseSpeed, warning) * TAU) * 0.08;
    this._updateAnchorVisual(dt, 1, pulse + warning * 0.55, warning, 1);
    this._updateTrail(saturate(1 - this._trailLingerTime / Math.max(0.05, c.trailLinger)), 1, 0);
    this._updateDistortion(0.22 + warning * 0.42, 0.6 + warning * 0.8, false);
    this._anchorFx(dt, warning);
    this.position.copy(this.ctx.character?.position ?? this._destination)
      .add(this._anchorPosition)
      .multiplyScalar(0.5)
      .setY(0.9);
    this.u = 0.5;

    if (ratio >= 1) {
      this._expiryFx();
      this._beginDissipation('expire');
    }
  }

  _updateSwap(dt) {
    const c = settings.shadowstep;
    this._swapTime += dt;
    const connection = Math.max(0.01, c.connectionDuration);
    const disappear = Math.max(0.01, c.disappearTime * c.swapTimeScale);
    const reappear = Math.max(0.01, c.reappearTime * c.swapTimeScale);
    const total = connection + disappear + reappear;
    const connect = saturate(this._swapTime / connection);
    const vanish = saturate((this._swapTime - connection) / disappear);
    const rebuild = saturate((this._swapTime - connection - disappear) / reappear);
    const movement = smoothstep(0.08, 0.82, saturate((this._swapTime - connection * 0.72) / (disappear + reappear)));

    _pos.copy(this._swapOrigin).lerp(this._anchorPosition, Easing.inOutCubic(movement));
    this._moveCharacter(_pos);
    this.position.copy(_pos).setY(0.9);

    let opacity = 1;
    let compression = Math.sin(connect * Math.PI) * 0.28;
    if (this._swapTime >= connection) {
      opacity = 1 - Easing.inCubic(saturate((vanish - 0.08) / 0.72));
      compression = Math.sin(vanish * Math.PI) * (1 - rebuild);
    }
    if (this._swapTime >= connection + disappear) opacity = Easing.outCubic(rebuild);
    this._setCharacterVisual(opacity, compression);

    if (!this._swapPeakFired && movement >= 0.48) {
      this._swapPeakFired = true;
      this._swapApexFx();
    }

    const apex = Math.sin(Math.PI * saturate(this._swapTime / total));
    this._updateAnchorVisual(dt, 1, 0.55 + connect * 0.75 + apex * 0.35, connect * 0.45, 1);
    this._updateTrail(Math.max(0.25, apex), saturate(0.5 + connect * 0.5), 1);
    this._updateDistortion(0.5 + apex * 0.5, 1.1 + apex * 0.9, true);
    this._pathFx(dt, movement, 1);
    this.ctx.shake.rumble(c.swapRumble * apex * settings.global.cameraShake, dt);

    if (this._swapTime >= total) {
      this._moveCharacter(this._anchorPosition);
      this._setCharacterVisual(1, 0);
      _dir.copy(this.direction).multiplyScalar(-1);
      this._arrivalFx(this._anchorPosition, _dir);
      this._beginDissipation('swap');
    }
  }

  _beginDissipation(mode) {
    this._mode = mode;
    this._residualActive = false;
    this.phase = AbilityPhase.FADE;
    this.fadeTime = 0;
    if (!this._cooldownStarted) {
      this._cooldownStarted = true;
      this.ctx.onCooldownStart?.(this.element, settings.shadowstep.cooldown);
    }
  }

  _updateDissipation(dt) {
    this.fadeTime += dt;
    const t = saturate(this.fadeTime / this.fadeDuration);
    const fade = 1 - Easing.inCubic(t);
    const consumed = this._mode === 'swap';
    this._updateAnchorVisual(dt, consumed ? fade * 0.45 : fade, consumed ? 1.2 : 0.35, 0.9, fade);
    this._updateTrail(fade * 0.55, 1, consumed ? 1 : 0);
    this._updateDistortion(fade * 0.45, fade, consumed);
    this._anchorFx(dt, 1 - fade);
    this.position.copy(this._anchorPosition).setY(0.9);
    if (t >= 1) this.phase = AbilityPhase.DONE;
  }

  _poseBodyInstances() {
    const c = settings.shadowstep;
    for (let index = 0; index < BODY_COUNT; index++) {
      const part = BODY_PARTS[index];
      _dummy.position.set(part[0], part[1], part[2]);
      _dummy.scale.set(part[3], part[4], part[5]);
      _dummy.rotation.set(part[6], part[7], part[8]);
      _dummy.updateMatrix();
      for (const mesh of this.bodyMeshes) mesh.setMatrixAt(index, _dummy.matrix);
    }
    for (const mesh of this.bodyMeshes) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.scale.setScalar(c.silhouetteScale);
    }

    for (let index = 0; index < 2; index++) {
      _dummy.position.set(index === 0 ? -0.082 : 0.082, 1.61, 0.185);
      _dummy.scale.set(0.036, 0.025, 0.025);
      _dummy.rotation.set(0, 0, 0);
      _dummy.updateMatrix();
      this.eyes.setMatrixAt(index, _dummy.matrix);
    }
    this.eyes.instanceMatrix.needsUpdate = true;
    this.eyes.scale.setScalar(c.silhouetteScale);
  }

  _updateAnchorVisual(dt, reveal, pulse, instability, fade) {
    const c = settings.shadowstep;
    this._poseBodyInstances();
    const yaw = Math.atan2(this.direction.x, this.direction.z);
    const drift = c.afterimageDrift * (0.35 + instability);
    this.silhouette.position.copy(this._anchorPosition);
    this.outline.position.copy(this._anchorPosition);
    this.eyes.position.copy(this._anchorPosition);
    this.fragments.position.copy(this._anchorPosition);
    this.afterimageA.position.copy(this._anchorPosition).addScaledVector(this.side, Math.sin(this.age * 1.7) * drift);
    this.afterimageA.position.y += 0.025 + Math.sin(this.age * 2.1) * 0.035;
    this.afterimageB.position.copy(this._anchorPosition).addScaledVector(this.side, -Math.sin(this.age * 1.35 + 1.2) * drift * 0.75);
    this.afterimageB.position.y -= 0.015;
    for (const mesh of this.anchorPoseMeshes) mesh.rotation.y = yaw;
    this.outline.scale.setScalar(c.silhouetteScale * (1.06 + pulse * 0.018));
    this.afterimageA.scale.setScalar(c.silhouetteScale * (0.985 + Math.sin(this.age * 2.4) * 0.015));
    this.afterimageB.scale.setScalar(c.silhouetteScale * 1.018);

    const state = this._visualState;
    state.seed = this._seed;
    state.reveal = reveal;
    state.pulse = pulse;
    state.instability = instability;
    state.fade = fade;
    state.opacity = 1;
    syncShadowMaterial(this.silhouetteMaterial, c, state);
    state.opacity = c.outlineOpacity;
    syncShadowMaterial(this.outlineMaterial, c, state);
    state.opacity = c.afterimageOpacity * (0.55 + instability * 0.45);
    syncShadowMaterial(this.afterimageMaterialA, c, state);
    state.opacity *= 0.72;
    syncShadowMaterial(this.afterimageMaterialB, c, state);
    state.opacity = c.eyeIntensity * (0.7 + pulse * 0.3);
    state.pulse = 1.4 + pulse;
    syncShadowMaterial(this.eyeMaterial, c, state);
    state.opacity = c.fragmentOpacity;
    state.pulse = pulse;
    syncShadowMaterial(this.fragmentMaterial, c, state);

    const count = Math.min(MAX_FRAGMENTS, Math.max(0, Math.round(c.fragmentCount)));
    for (let index = 0; index < count; index++) {
      const record = this.fragmentRecords[index];
      const angle = record.angle + this.age * c.fragmentOrbitSpeed * record.speed * (index % 2 ? -1 : 1);
      const radius = c.fragmentOrbitRadius * lerp(0.45, 1, record.radius) * (1 - instability * 0.14);
      _dummy.position.set(
        Math.cos(angle) * radius,
        lerp(0.24, 1.82, record.height) + Math.sin(this.age * 2.3 + record.phase) * 0.1,
        Math.sin(angle) * radius
      );
      const size = c.fragmentSize * lerp(0.45, 1.25, record.size) * (1 + instability * 0.45);
      _dummy.scale.set(size * 0.55, size * 1.45, size * 0.42);
      _dummy.rotation.set(angle * 0.7, angle * 1.3, this.age * record.speed * 2.0);
      _dummy.updateMatrix();
      this.fragments.setMatrixAt(index, _dummy.matrix);
    }
    this.fragments.count = count;
    if (count > 0) this.fragments.instanceMatrix.needsUpdate = true;

    const groundScale = c.groundShadowRadius * (1 + pulse * 0.035);
    this.groundEcho.position.copy(this._anchorPosition).setY(c.groundHeight);
    this.groundEcho.scale.set(groundScale, groundScale, 1);
    state.opacity = c.groundOpacity;
    state.progress = 1;
    state.bidirectional = 1;
    syncShadowMaterial(this.groundMaterial, c, state);
    this._setAnchorVisible(reveal * fade > 0.002);
  }

  _setAnchorVisible(visible) {
    for (const mesh of this.anchorPoseMeshes) mesh.visible = visible;
    this.groundEcho.visible = visible;
  }

  _updateTrail(fade, progress, bidirectional) {
    const c = settings.shadowstep;
    if (fade <= 0.002) {
      this.trailRibbon.clear();
      this.trailCoreRibbon.clear();
      this.trail.visible = false;
      this.trailCore.visible = false;
      return;
    }
    for (let index = 0; index <= TRAIL_SEGMENTS; index++) {
      const t = index / TRAIL_SEGMENTS;
      const point = this.trailPoints[index];
      point.copy(this._pathStart).lerp(this._pathEnd, t);
      const envelope = Math.sin(Math.PI * t);
      const waveA = Math.sin(t * c.trailWaves * TAU + this._seed + this.age * c.shaderFlow);
      const waveB = Math.sin(t * (c.trailWaves * 0.63) * TAU - this.age * c.shaderFlow * 1.35 + this._seed * 0.4);
      point.addScaledVector(this.side, envelope * c.trailTurbulence * (waveA * 0.24 + waveB * 0.13));
      point.y += envelope * (c.trailLift + waveB * c.trailVerticalTurbulence);
    }
    this._trailOptions.width = c.trailWidth;
    this._trailCoreOptions.width = c.trailCoreWidth;
    this.trailRibbon.build(this.trailPoints, this._trailOptions);
    this.trailCoreRibbon.build(this.trailPoints, this._trailCoreOptions);
    this.trail.visible = true;
    this.trailCore.visible = true;
    const state = this._visualState;
    state.seed = this._seed;
    state.fade = fade;
    state.opacity = c.trailOpacity;
    state.progress = progress;
    state.bidirectional = bidirectional;
    state.pulse = 0;
    syncShadowMaterial(this.trailMaterial, c, state);
    state.opacity = c.trailCoreOpacity;
    syncShadowMaterial(this.trailCoreMaterial, c, state);
  }

  _updateDistortion(fade, pulse, bothEnds) {
    const c = settings.shadowstep;
    if (fade <= 0.002) {
      this.distortion.count = 0;
      return;
    }
    const count = bothEnds ? 2 : 1;
    for (let index = 0; index < count; index++) {
      const at = bothEnds && index === 1 ? this._pathEnd : this._anchorPosition;
      _dummy.position.copy(at).setY(c.distortionHeight);
      const size = c.distortionRadius * (index === 0 ? 1 : 0.82) * (0.8 + pulse * 0.2);
      _distortionScale.set(size, size * c.distortionVerticalScale, size);
      _dummy.scale.copy(_distortionScale);
      _dummy.rotation.set(0, this.age * 0.7 + index, 0);
      _dummy.updateMatrix();
      this.distortion.setMatrixAt(index, _dummy.matrix);
    }
    this.distortion.count = count;
    this.distortion.instanceMatrix.needsUpdate = true;
    const state = this._visualState;
    state.fade = fade;
    state.pulse = pulse;
    state.intensity = pulse;
    syncShadowMaterial(this.distortionMaterial, c, state);
  }

  _syncParticleUniforms() {
    const c = settings.shadowstep;
    const g = settings.global;
    this.motes.setGradient(getColor(c.colorCore), getColor(c.colorSpark), getColor(c.colorEdge), getColor(c.colorDark));
    this.motes.uniforms.uGravity.value.set(0, c.moteRise, 0);
    this.motes.uniforms.uSizeScale.value = c.moteSize * g.particleSize * 7;
    this.motes.uniforms.uLifeScale.value = c.moteLifetime * 0.5 * g.particleLifetime;
    this.motes.uniforms.uSpeedScale.value = g.particleSpeed;
    this.motes.uniforms.uOpacity.value = c.moteOpacity * g.opacity;
    this.motes.uniforms.uGlow.value = c.moteGlow * g.glow;
    this.motes.uniforms.uTurbulence.value = c.particleTurbulence * g.turbulence;
    this.motes.uniforms.uSwirl.value = c.particleSwirl;
    this.motes.uniforms.uSwirlExpand.value = c.particleSwirlExpand;

    this.smoke.setGradient(getColor(c.colorSmokeA), getColor(c.colorSmokeB), getColor(c.colorSmokeC), getColor(c.colorSmokeD));
    this.smoke.uniforms.uGravity.value.set(0, c.smokeRise, 0);
    this.smoke.uniforms.uSizeScale.value = c.smokeSize * g.particleSize;
    this.smoke.uniforms.uLifeScale.value = c.smokeLifetime * 0.5 * g.particleLifetime;
    this.smoke.uniforms.uSpeedScale.value = g.particleSpeed;
    this.smoke.uniforms.uOpacity.value = c.smokeOpacity * g.opacity;
    this.smoke.uniforms.uTurbulence.value = c.smokeTurbulence * g.turbulence;

    this.streaks.setGradient(getColor(c.colorCore), getColor(c.colorSpark), getColor(c.colorEdge), getColor(c.colorDark));
    this.streaks.uniforms.uGravity.value.set(0, c.streakGravity, 0);
    this.streaks.uniforms.uSizeScale.value = c.streakSize * g.particleSize * 7;
    this.streaks.uniforms.uLifeScale.value = c.streakLifetime * 0.5 * g.particleLifetime;
    this.streaks.uniforms.uSpeedScale.value = g.particleSpeed;
    this.streaks.uniforms.uOpacity.value = c.streakOpacity * g.opacity;
    this.streaks.uniforms.uGlow.value = c.streakGlow * g.glow;
    this.streaks.uniforms.uStretch.value = c.streakStretch;
    this.streaks.uniforms.uTurbulence.value = c.particleTurbulence * g.turbulence;

    this.chips.setGradient(getColor(c.colorFragmentA), getColor(c.colorFragmentB), getColor(c.colorFragmentC), getColor(c.colorFragmentD));
    this.chips.uniforms.uGravity.value.set(0, c.fragmentGravity, 0);
    this.chips.uniforms.uSizeScale.value = c.chipSize * g.particleSize * 7;
    this.chips.uniforms.uLifeScale.value = c.chipLifetime * g.particleLifetime;
    this.chips.uniforms.uSpeedScale.value = g.particleSpeed;
    this.chips.uniforms.uOpacity.value = c.chipOpacity * g.opacity;
  }

  _prepFx(dt, at, intensity) {
    const c = settings.shadowstep;
    let count = this.intakeEmitter.tick(dt, c.intakeRate * (0.35 + intensity));
    const per = Math.max(1, Math.ceil(count / 5));
    while (count > 0) {
      const angle = Math.random() * TAU;
      const radius = c.intakeRadius * randRange(0.72, 1.15);
      _pos.set(at.x + Math.cos(angle) * radius, randRange(0.1, 1.65), at.z + Math.sin(angle) * radius);
      _dir.copy(at).setY(randRange(0.55, 1.25)).sub(_pos).normalize();
      this._emit(
        this.motes, Math.min(per, count), _pos, _dir,
        0.08, c.intakeSpeed, c.moteSize, c.intakeLifetime, 0.12, at
      );
      count -= per;
    }
  }

  _anchorFx(dt, warning) {
    const c = settings.shadowstep;
    const moteCount = this.anchorMoteEmitter.tick(dt, c.anchorMoteRate * (1 + warning * 1.4));
    if (moteCount > 0) {
      _pos.copy(this._anchorPosition).setY(randRange(0.25, 1.55));
      _dir.set(randRange(-0.25, 0.25), 1, randRange(-0.25, 0.25)).normalize();
      this._emit(
        this.motes, moteCount, _pos, _dir,
        c.silhouetteScale * 0.34, c.anchorMoteSpeed * (1 + warning),
        c.moteSize, c.moteLifetime, 0.65, this._anchorPosition
      );
    }
    const smokeCount = this.smokeEmitter.tick(dt, c.smokeRate * (1 + warning * 0.65));
    if (smokeCount > 0) {
      _pos.copy(this._anchorPosition).setY(0.08);
      _dir.set(0, 1, 0);
      this._emit(
        this.smoke, smokeCount, _pos, _dir,
        c.groundShadowRadius * 0.62, c.smokeSpeed, c.smokeSize, c.smokeLifetime,
        0.95, null, 0.5
      );
    }
  }

  _pathFx(dt, progress, bidirectional) {
    const c = settings.shadowstep;
    const count = this.pathEmitter.tick(dt, c.pathParticleRate * (0.55 + bidirectional * 0.7));
    if (count <= 0) return;
    const batches = Math.min(6, count);
    const per = Math.ceil(count / batches);
    let left = count;
    while (left > 0) {
      const t = bidirectional ? Math.random() : Math.random() * Math.max(0.08, progress);
      _pos.copy(this._pathStart).lerp(this._pathEnd, t);
      _pos.addScaledVector(this.side, Math.sin(t * c.trailWaves * TAU + this.age * c.shaderFlow) * c.trailWidth * 0.25);
      _dir.copy(this._pathEnd).sub(this._pathStart).normalize();
      if (bidirectional && Math.random() < 0.5) _dir.multiplyScalar(-1);
      this._emit(
        this.streaks, Math.min(per, left), _pos, _dir,
        c.trailWidth * 0.45, c.pathParticleSpeed * randRange(0.65, 1.35),
        c.streakSize, c.streakLifetime, 0.32
      );
      left -= per;
    }
  }

  _emit(
    system,
    count,
    position,
    direction,
    radius = 0,
    speed = 1,
    size = 0.08,
    life = 0.8,
    spread = 0.65,
    anchor = null,
    spin = 0,
    speedVariance = 0.5,
    sizeVariance = 0.58,
    lifeVariance = 0.42
  ) {
    _emit.position = position;
    _emit.radius = radius;
    _emit.direction = direction;
    _emit.speed = speed;
    _emit.speedVariance = speedVariance;
    _emit.spread = spread;
    _emit.inherit = null;
    _emit.anchor = anchor;
    _emit.size = size;
    _emit.sizeVariance = sizeVariance;
    _emit.life = life;
    _emit.lifeVariance = lifeVariance;
    _emit.spin = spin;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    _emit.owner = this._particleOwner;
    system.emit(Math.round(count * settings.global.particleCount), _emit);
  }

  _castStartFx() {
    const c = settings.shadowstep;
    this.ctx.flash.trigger(getColor(c.colorFlash), c.castFlash * settings.global.explosionIntensity);
    this.lightBoost = c.lightIntensity * c.castLightBoost;
  }

  _vanishFx(at, direction) {
    const c = settings.shadowstep;
    const g = settings.global;
    _pos.copy(at).setY(c.burstHeight);
    this.ctx.bursts.spawn(BurstMode.STORM, _pos, {
      radius: c.vanishBurstRadius,
      endRadius: c.vanishBurstEnd * g.explosionIntensity,
      life: c.vanishBurstLife,
      intensity: c.burstIntensity,
      opacity: c.burstOpacity,
      fresnel: 1.8,
      displace: c.burstDisplace,
      colorA: getColor(c.colorShadow),
      colorB: getColor(c.colorEdge),
      colorC: getColor(c.colorCore)
    });
    _dir.copy(direction).setY(0.18).normalize();
    this._emit(
      this.streaks, c.vanishParticles, _pos, _dir,
      c.vanishBurstRadius, c.pathParticleSpeed * 1.35,
      c.streakSize * 1.25, c.streakLifetime * 1.1, 0.55
    );
    this._emit(
      this.chips, c.vanishFragments, _pos, _dir,
      0.35, c.fragmentBurstSpeed, c.chipSize, c.chipLifetime, 0.9, null, 9
    );
    this.ctx.shake.add(c.castShake * g.cameraShake, 1 / Math.max(0.1, c.shakeDuration), 28);
  }

  _arrivalFx(at, direction) {
    const c = settings.shadowstep;
    const g = settings.global;
    _pos.copy(at).setY(c.burstHeight);
    this.ctx.bursts.spawn(BurstMode.STORM, _pos, {
      radius: c.arrivalBurstRadius,
      endRadius: c.arrivalBurstEnd * g.explosionIntensity,
      life: c.arrivalBurstLife,
      intensity: c.burstIntensity * 1.15,
      opacity: c.burstOpacity,
      fresnel: 2,
      displace: c.burstDisplace,
      squash: 0.82,
      colorA: getColor(c.colorShadow),
      colorB: getColor(c.colorSpark),
      colorC: getColor(c.colorCore)
    });
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, at, {
      radius: c.arrivalRingRadius * g.explosionIntensity,
      life: c.arrivalRingLife,
      width: c.arrivalRingWidth,
      intensity: c.arrivalRingIntensity,
      colorA: getColor(c.colorEdge),
      colorB: getColor(c.colorCore)
    });
    _dir.copy(direction).setY(0.45).normalize();
    this._emit(
      this.motes, c.arrivalParticles, _pos, _dir,
      0.35, c.arrivalParticleSpeed, c.moteSize * 1.25, c.moteLifetime, 1
    );
    this.ctx.flash.trigger(getColor(c.colorFlash), c.arrivalFlash * g.explosionIntensity);
    this.lightBoost = c.lightIntensity * c.arrivalLightBoost;
  }

  _swapReactionFx() {
    const c = settings.shadowstep;
    _pos.copy(this._anchorPosition).setY(c.burstHeight);
    _dir.set(0, 1, 0);
    this._emit(
      this.motes, c.swapIntakeParticles, _pos, _dir,
      c.intakeRadius, c.intakeSpeed * 1.4, c.moteSize * 1.25,
      c.connectionDuration * 1.5, 1, this._anchorPosition
    );
    this.lightBoost = c.lightIntensity * c.swapLightBoost;
  }

  _swapApexFx() {
    const c = settings.shadowstep;
    const g = settings.global;
    _pos.copy(this._anchorPosition).setY(c.burstHeight);
    this.ctx.bursts.spawn(BurstMode.STORM, _pos, {
      radius: c.swapBurstRadius,
      endRadius: c.swapBurstEnd * g.explosionIntensity,
      life: c.swapBurstLife,
      intensity: c.burstIntensity * 1.55,
      opacity: c.burstOpacity,
      fresnel: 2.2,
      displace: c.burstDisplace * 1.25,
      colorA: getColor(c.colorShadow),
      colorB: getColor(c.colorEdge),
      colorC: getColor(c.colorCore)
    });
    this.ctx.flash.trigger(getColor(c.colorSwapFlash), c.swapFlash * g.explosionIntensity);
    this.ctx.shake.add(c.swapShake * g.cameraShake, 1 / Math.max(0.1, c.shakeDuration), 34);
    this.lightBoost = c.lightIntensity * c.swapApexLightBoost;
  }

  _expiryFx() {
    const c = settings.shadowstep;
    _pos.copy(this._anchorPosition).setY(c.burstHeight * 0.65);
    _dir.set(0, 1, 0);
    this._emit(
      this.chips, c.expiryFragments, _pos, _dir,
      c.silhouetteScale * 0.35, c.fragmentBurstSpeed * 0.7,
      c.chipSize, c.dissipateDuration, 1, null, 7
    );
  }

  _setCharacterVisual(opacity, compression) {
    this.ctx.character?.setShadowTransition?.(opacity, compression);
  }

  _moveCharacter(position) {
    if (this.ctx.character?.moveTo) this.ctx.character.moveTo(position);
    else if (this.ctx.character?.position) this.ctx.character.position.copy(position);
  }

  onDestroy() {
    this._residualActive = false;
    this._mode = 'idle';
    this._setCharacterVisual(1, 0);
    this._setAnchorVisible(false);
    this.fragments.count = 0;
    this.distortion.count = 0;
    this.trailRibbon.clear();
    this.trailCoreRibbon.clear();
    this.trail.visible = false;
    this.trailCore.visible = false;
    this.ctx.particles.clearOwner?.(this._particleOwner, frame.uTime.value, this._ownedParticleSystems);
  }

  dispose() {
    this.bodyGeometry.dispose();
    this.fragmentGeometry.dispose();
    this.distortionGeometry.dispose();
    this.groundGeometry.dispose();
    this.trailRibbon.dispose();
    this.trailCoreRibbon.dispose();
    for (const material of [
      this.silhouetteMaterial,
      this.outlineMaterial,
      this.afterimageMaterialA,
      this.afterimageMaterialB,
      this.eyeMaterial,
      this.fragmentMaterial,
      this.trailMaterial,
      this.trailCoreMaterial,
      this.groundMaterial,
      this.distortionMaterial
    ]) material.dispose();
    super.dispose();
  }
}
